// ============================================
// Settings — L10n refresh, slider bindings, language popup, toggles, arousal breathing
// Extracted from main.js
// ============================================

import { events, Events } from '../engine/events.js';
import { audioManager } from '../systems/audio.js';
import { L } from '../systems/localization.js';
import { variableManager } from '../engine/variables.js';
import { cloudSaveManager } from '../systems/cloud-save.js';
import { initContentPrefToggles, contentMenuEnabled, hasVisibleContentTracks } from './content-tracks.js';
import { isPat } from '../engine/pgate.js';

// ============================================
// Unified Text & UI Size slider
// (v0.2.5.7.7 — replaces the previous Font Size + UI Scale split. The
// two sliders measured the same player intent ("make things readable")
// but with different mechanisms, leading to confusion when one didn't
// cover popups (foundby:Discord player 2026-05-05). One slider now
// drives all three effects: --font-size-base (story text), --overlay-zoom
// (popups/tooltips/modals), and the game-view CSS `zoom` (canvas).
//
// PnO_TextScale is the canonical setting, slider range 0.8–3.0 (80%-300%).
// The 300% ceiling exists for very high-resolution screens (4K) where even
// 200% reads small; _applyTextScale's safe-clamp upper bound MUST match the
// slider max or values above it silently snap back to 1.0.
// Legacy PnO_FontSize / PnO_UIScale are migrated on first read.)
// ============================================

/** Apply a 0.8–1.5 scale value to all three readability targets. */
function _applyTextScale(scale) {
    const safe = (typeof scale === 'number' && scale >= 0.5 && scale <= 3) ? scale : 1;

    // Story text font (clamped to 12-22px range to match the legacy slider's
    // visual feel — past that it overflows the story panel on small screens).
    const fontPx = Math.max(12, Math.min(22, Math.round(16 * safe)));
    document.documentElement.style.setProperty('--font-size-base', fontPx + 'px');

    // Overlay/popup/tooltip zoom — see BUG_PATTERNS § 47 for why this is
    // transform: scale() on inner containers, not CSS zoom.
    document.documentElement.style.setProperty('--overlay-zoom', String(safe));

    // Game-view canvas (sidepanels, hotspots, choice buttons). CSS `zoom`
    // here is fine because these are NOT position:fixed roots.
    const gameView = document.getElementById('screen-game-view');
    const mainMenu = document.getElementById('screen-main-menu');
    const editor = document.getElementById('screen-character-editor');
    const backdrop = document.getElementById('overlay-backdrop');
    if (gameView) gameView.style.zoom = safe;
    if (mainMenu) mainMenu.style.zoom = safe;
    // Character editor honors the same Text-Size scale (non-fixed root, so CSS
    // zoom is safe here too — matches menu/game-view treatment).
    if (editor) editor.style.zoom = safe;
    if (backdrop) backdrop.style.zoom = safe;
}

/** Largest UI scale the CURRENT viewport can show without the game-view zoom
 *  clipping the topbar (logo + week-strip + time-slots + menu buttons can't
 *  shrink past ~the full width) or pushing the left column's phone/wardrobe
 *  buttons below the fold. zoom S shrinks the effective CSS viewport to
 *  (w/S, h/S), so a 1920x1080 screen has essentially NO upscale headroom,
 *  it must stay 1.0. Only genuinely large desktops (QHD/4K-wide at low DPR)
 *  can grow. Returns >= 1.0; never forces a shrink. */
function _maxComfortableScale() {
    try {
        const w = window.innerWidth, h = window.innerHeight;
        // Headroom is purely a CSS-pixel question: `zoom` operates in CSS px, so
        // a 2560-CSS-px-wide viewport has the same room whether the display is
        // DPR 1 or a Retina DPR 2 (the browser already reports CSS px in
        // innerWidth/innerHeight). The old `dpr <= 1.25` guard wrongly denied
        // every Retina/Safari large display and returned 1.0, which clamped a
        // legacy-settings player's migrated scale down to 1.0 on update
        // (foundby:release-gate webkit 0.2.8.1.1 — real impact: 5K iMac /
        // Studio Display Safari users with old PnO_FontSize + PnO_UIScale keys).
        if (w >= 2400 && h >= 1300) return 1.3;  // QHD/4K-wide-class CSS viewport
        if (w >= 2000 && h >= 1250) return 1.15;
    } catch (e) {}
    return 1.0;  // 1080p-class and smaller: no headroom, 1.0 keeps everything on-screen
}

/** Migrate legacy PnO_FontSize + PnO_UIScale into the new PnO_TextScale.
 *  Called once on bindSettingsSliders() init. Idempotent: if PnO_TextScale
 *  already exists, the migration is skipped. */
function _migrateLegacyReadabilityKeys() {
    try {
        if (localStorage.getItem('PnO_TextScale') !== null) return; // already migrated
        const fs = parseFloat(localStorage.getItem('PnO_FontSize'));
        const us = parseFloat(localStorage.getItem('PnO_UIScale'));
        if (Number.isNaN(fs) && Number.isNaN(us)) return; // nothing to migrate

        // Convert legacy values to a unified 0.8-1.5 scale.
        const fsScale = !Number.isNaN(fs) && fs > 0 ? fs / 16 : 1;
        const usScale = !Number.isNaN(us) && us > 0 ? us : 1;
        // Geometric mean — preserves 1.0 = 1.0 and softly merges divergent
        // settings (e.g. font 18 + ui 1.2 → ~1.17 instead of harsh 1.35).
        const merged = Math.sqrt(fsScale * usScale);
        let clamped = Math.max(0.8, Math.min(1.5, Math.round(merged * 20) / 20));
        // Cap by what the viewport can actually show. The vast majority of
        // legacy PnO_UIScale values are the OLD broken width-only auto-detect
        // (saved 1.2 on every 1920px screen incl. 1080p), so carrying them
        // forward kept the canvas oversized + the topbar/phone clipped.
        // Never caps below 1.0 (a manual shrink stays). foundby:CutieLvr.
        clamped = Math.min(clamped, _maxComfortableScale());
        localStorage.setItem('PnO_TextScale', String(clamped));
        // Drop legacy keys so a subsequent boot doesn't re-migrate stale state.
        localStorage.removeItem('PnO_FontSize');
        localStorage.removeItem('PnO_UIScale');
    } catch (e) { console.warn('[Settings] readability migration failed:', e?.message); }
}

/** First-run auto-scale for high-DPI monitors. Only fires when no scale has
 *  been set yet (fresh install, post-migration with no legacy keys). Writes
 *  PnO_TextScale so the unified system stays the single source of truth.
 *
 *  HEIGHT-AWARE on purpose: the previous main.js auto-detect keyed on WIDTH
 *  only (w >= 1800 then 1.2), which caught 1920x1080 @ DPR1, the single most
 *  common desktop resolution. zoom 1.2 made the game-view 120dvh tall, body
 *  overflow:hidden clipped the bottom 20% (the phone/wardrobe buttons at the
 *  foot of the left column) with no way to scroll to them. Only upscale when
 *  BOTH dimensions have headroom so the zoomed layout still fits vertically.
 *  foundby:CutieLvr 0.2.7.3 (1920x1080 PC, phone unreachable). */
function _firstRunAutoScale() {
    try {
        if (localStorage.getItem('PnO_TextScale') !== null) return; // already set / migrated
        const scale = _maxComfortableScale();
        // 1920x1080 (and anything 1080p-tall or shorter) stays 1.0, no clip.
        if (scale !== 1.0) {
            localStorage.setItem('PnO_TextScale', String(scale));
            console.log(`[Settings] First-run auto-scale: ${scale} (${window.innerWidth}x${window.innerHeight} @ ${window.devicePixelRatio}x DPR)`);
        }
    } catch (e) { console.warn('[Settings] first-run auto-scale failed:', e?.message); }
}

/** Re-apply persisted scale (used by bindSettingsSliders init AND any
 *  external caller that needs to refresh the scale). */
function _applyPersistedTextScale() {
    let scale = 1;
    try {
        const s = parseFloat(localStorage.getItem('PnO_TextScale'));
        if (!Number.isNaN(s) && s > 0) scale = s;
    } catch (e) {}
    _applyTextScale(scale);
}

// Backwards-compat shim: existing call sites elsewhere in settings.js
// (and any future ones) can keep calling _applyOverlayZoom().
function _applyOverlayZoom() { _applyPersistedTextScale(); }

// ============================================
// Screen Edge Buffer (v0.2.8 — player ask 0.2.7.6.1, Pixel 9 landscape:
// pinhole camera / Android back-gesture zone covered the UI. env(safe-area-
// inset-*) reports 0 in most Android WebViews for camera cutouts in
// landscape, so an OS-independent manual buffer is needed. The CSS var is
// consumed in _00-base.css as
//   padding-left/right: max(var(--edge-buffer), env(safe-area-inset-*))
// so the user value stacks with (never fights) the OS safe-area.)
// PnO_EdgeBuffer is the canonical setting, value in px (0-40).
// ============================================

function _applyEdgeBuffer(px) {
    const safe = (typeof px === 'number' && px >= 0 && px <= 80) ? Math.round(px) : 0;
    document.documentElement.style.setProperty('--edge-buffer', safe + 'px');
}

function _applyPersistedEdgeBuffer() {
    let px = 0;
    try {
        const v = parseInt(localStorage.getItem('PnO_EdgeBuffer'), 10);
        if (!Number.isNaN(v) && v > 0) px = v;
    } catch (e) {}
    _applyEdgeBuffer(px);
}

// ============================================
// L10n: update all data-l10n elements when language changes
// ============================================
function refreshL10nUI() {
    document.querySelectorAll('[data-l10n]').forEach(el => {
        const key = el.getAttribute('data-l10n');
        const translated = L.get(key);
        if (translated !== key) {
            // For <option> elements, set textContent; for others, set textContent
            el.textContent = translated;
        }
    });
    // Also update title attributes via data-l10n-title
    document.querySelectorAll('[data-l10n-title]').forEach(el => {
        const key = el.getAttribute('data-l10n-title');
        const translated = L.get(key);
        if (translated !== key) {
            el.title = translated;
        }
    });
    // Also update alt attributes via data-l10n-alt (used by stat icons)
    document.querySelectorAll('[data-l10n-alt]').forEach(el => {
        const key = el.getAttribute('data-l10n-alt');
        const translated = L.get(key);
        if (translated !== key) {
            el.alt = translated;
        }
    });
    // Update the Journal toggle button text
    const btnToggle = document.getElementById('btn-toggle-secondary');
    if (btnToggle) {
        const secondary = document.querySelector('.actions-secondary');
        const journalText = L.get('ui.journal');
        const arrow = secondary && !secondary.classList.contains('hidden') ? '\u25B2' : '\u25BC';
        btnToggle.innerHTML = journalText + ' ' + arrow;
    }
}

// Listen for language changes
events.on(Events.LOCALE_CHANGED || 'locale:changed', () => {
    refreshL10nUI();
});

// Build every language picker — the two Settings dropdowns (#menu-language,
// #pause-language) AND the first-run flag popup — from L.getAvailableLanguages(),
// the single source of truth. index.html ships EMPTY shells; this fills them.
// Idempotent, and called from BOTH bindSettingsSliders() and
// showLanguagePopupIfNeeded() (main.js runs them in either order), so whichever
// fires first builds the controls and the other is a harmless rebuild.
// Replaces the old hardcoded <option>/button lists that silently dropped Italian
// for ~2 months (foundby player 2026-07-04).
function populateLanguageControls() {
    const langs = (typeof L.getAvailableLanguages === 'function' ? L.getAvailableLanguages() : []) || [];
    if (!langs.length) return;

    // Settings dropdowns — safe to rebuild every call: the change listener lives
    // on the <select>, not on the <option>s.
    ['menu-language', 'pause-language'].forEach(id => {
        const sel = document.getElementById(id);
        if (!sel) return;
        const cur = sel.value || L.getLanguage();
        sel.innerHTML = '';
        for (const { code, name } of langs) {
            const o = document.createElement('option');
            o.value = code;
            o.textContent = name;
            sel.appendChild(o);
        }
        if (langs.some(l => l.code === cur)) sel.value = cur;
    });

    // First-run flag popup — build ONLY when empty, so we never strip the click
    // listeners showLanguagePopupIfNeeded() attaches to these buttons.
    const grid = document.querySelector('#language-popup .language-popup-grid');
    if (grid && !grid.querySelector('.language-popup-btn')) {
        for (const { code, name, flag } of langs) {
            const btn = document.createElement('button');
            btn.className = 'language-popup-btn';
            btn.dataset.lang = code;
            const f = document.createElement('span');
            f.className = 'lang-flag';
            f.textContent = flag || '';
            const n = document.createElement('span');
            n.className = 'lang-name';
            n.textContent = name;
            btn.append(f, n);
            grid.appendChild(btn);
        }
    }
}

function bindSettingsSliders() {
    // Migrate legacy PnO_FontSize + PnO_UIScale → PnO_TextScale on first
    // boot after upgrading. Idempotent.
    _migrateLegacyReadabilityKeys();
    // Fill the language dropdowns from the single source before we read/bind them.
    populateLanguageControls();

    // Height-aware auto-scale for high-DPI monitors when nothing is set yet.
    // (Replaces the broken width-only auto-detect that lived in main.js.)
    _firstRunAutoScale();

    // Apply persisted Text Scale up-front so popups opened before any
    // slider interaction (first-run language popup, welcome modal,
    // achievement toasts during boot) honour the user's saved settings.
    _applyPersistedTextScale();
    _applyPersistedEdgeBuffer();

    document.querySelectorAll('.settings-slider').forEach(nativeInput => {
        const valueEl = nativeInput.parentElement?.querySelector('.settings-value');
        const id = nativeInput.id;

        // Restore saved value from localStorage BEFORE building
        try {
            if (id.includes('bgm')) {
                const saved = localStorage.getItem('PnO_BGMVolume');
                if (saved !== null) nativeInput.value = Math.round(parseFloat(saved) * 100);
            }
            if (id.includes('sfx')) {
                const saved = localStorage.getItem('PnO_SFXVolume');
                if (saved !== null) nativeInput.value = Math.round(parseFloat(saved) * 100);
            }
            if (id.includes('hypno')) {
                // Source of truth is audioManager.hypnoVolume (persists 'HypnoVolume').
                nativeInput.value = Math.round(audioManager.hypnoVolume * 100);
            }
            if (id.includes('textspeed')) {
                const saved = localStorage.getItem('PnO_TextSpeed');
                if (saved !== null) nativeInput.value = Math.round(parseFloat(saved) * 100);
            }
            if (id.includes('textscale')) {
                const saved = localStorage.getItem('PnO_TextScale');
                if (saved !== null) {
                    const pct = Math.round(parseFloat(saved) * 100);
                    if (!Number.isNaN(pct)) nativeInput.value = pct;
                }
            }
            if (id.includes('edgebuffer')) {
                const saved = parseInt(localStorage.getItem('PnO_EdgeBuffer'), 10);
                if (!Number.isNaN(saved)) nativeInput.value = saved;
            }
        } catch (e) { console.warn('[Settings] slider preset read failed:', e?.message); }

        // Build custom slider DOM around native input
        const wrapper = document.createElement('div');
        wrapper.className = 'gsap-slider';
        nativeInput.parentNode.insertBefore(wrapper, nativeInput);

        nativeInput.classList.remove('settings-slider');
        nativeInput.classList.add('gsap-slider__native');
        wrapper.appendChild(nativeInput);

        const track = document.createElement('div');
        track.className = 'gsap-slider__track';
        const progress = document.createElement('div');
        progress.className = 'gsap-slider__progress';
        track.appendChild(progress);
        wrapper.appendChild(track);

        const thumb = document.createElement('div');
        thumb.className = 'gsap-slider__thumb';
        wrapper.appendChild(thumb);

        const marker = document.createElement('div');
        marker.className = 'gsap-slider__marker';
        wrapper.appendChild(marker);

        // Sync custom visuals to native input value
        const min = parseFloat(nativeInput.min) || 0;
        const max = parseFloat(nativeInput.max) || 100;

        const syncVisuals = (animate = true) => {
            const val = parseFloat(nativeInput.value);
            const pct = (val - min) / (max - min);
            const dur = animate ? 0.25 : 0;

            gsap.to(progress, { scaleX: pct, duration: dur, ease: 'power2.out' });
            gsap.to(thumb, { left: `${pct * 100}%`, duration: dur, ease: 'power2.out' });
            gsap.to(marker, { left: `${pct * 100}%`, duration: dur, ease: 'power2.out' });
        };

        const _ts = (k, fb) => { const v = L.get(k); return v !== k ? v : fb; };
        const getDisplayValue = () => {
            const val = parseInt(nativeInput.value, 10);
            if (id.includes('textspeed')) {
                if (val === 0) return _ts('settings.textspeed.instant', 'Instant');
                if (val < 35) return _ts('settings.textspeed.fast', 'Fast');
                if (val < 65) return _ts('settings.textspeed.normal', 'Normal');
                return _ts('settings.textspeed.slow', 'Slow');
            }
            if (id.includes('edgebuffer')) {
                if (val === 0) return _ts('settings.edgebuffer.off', 'Off');
                return `${val}px`;
            }
            return `${val}%`;
        };

        const updateLabel = () => {
            const display = getDisplayValue();
            if (valueEl) valueEl.textContent = display;
            marker.textContent = display;
        };

        const persistAndApply = () => {
            const val = parseInt(nativeInput.value, 10);
            try {
                if (id.includes('bgm')) {
                    localStorage.setItem('PnO_BGMVolume', val / 100);
                    audioManager.bgmVolume = val / 100;
                }
                if (id.includes('sfx')) {
                    localStorage.setItem('PnO_SFXVolume', val / 100);
                    audioManager.sfxVolume = val / 100;
                }
                if (id.includes('hypno')) {
                    // Dedicated hypno-voice channel; the setter persists 'HypnoVolume'
                    // and live-applies to a playing, unmuted voice.
                    audioManager.hypnoVolume = val / 100;
                }
                if (id.includes('textspeed')) localStorage.setItem('PnO_TextSpeed', val / 100);
                if (id.includes('textscale')) {
                    const scale = val / 100;
                    localStorage.setItem('PnO_TextScale', String(scale));
                    _applyTextScale(scale);
                }
                if (id.includes('edgebuffer')) {
                    localStorage.setItem('PnO_EdgeBuffer', String(val));
                    _applyEdgeBuffer(val);
                }
            } catch (e) { console.warn('[Settings] slider value persist failed:', e?.message); }
        };

        // Show/hide floating marker on interaction
        const showMarker = () => gsap.to(marker, { opacity: 1, y: 0, duration: 0.2, ease: 'back.out(2)' });
        const hideMarker = () => gsap.to(marker, { opacity: 0, y: 4, duration: 0.15, ease: 'power2.in' });

        nativeInput.addEventListener('mousedown', showMarker);
        nativeInput.addEventListener('touchstart', showMarker, { passive: true });
        nativeInput.addEventListener('mouseup', hideMarker);
        nativeInput.addEventListener('touchend', hideMarker);
        nativeInput.addEventListener('mouseleave', hideMarker);

        nativeInput.addEventListener('input', () => { syncVisuals(false); updateLabel(); persistAndApply(); });

        // Init state (no animation on first render)
        syncVisuals(false);
        updateLabel();
    });

    // Sync all language dropdowns helper
    const syncAllLangDropdowns = (newLang, sourceId) => {
        ['pause-language', 'menu-language'].forEach(id => {
            if (id !== sourceId) {
                const el = document.getElementById(id);
                if (el) el.value = newLang;
            }
        });
    };

    // Language dropdown in pause menu
    const langSelect = document.getElementById('pause-language');
    if (langSelect) {
        langSelect.value = L.getLanguage();
        langSelect.addEventListener('change', async () => {
            await L.setLanguage(langSelect.value);
            syncAllLangDropdowns(langSelect.value, 'pause-language');
            console.log(`[PnO] Language changed to: ${langSelect.value}`);
        });
    }

    // Language dropdown in main menu settings
    const menuLangSelect = document.getElementById('menu-language');
    if (menuLangSelect) {
        menuLangSelect.value = L.getLanguage();
        menuLangSelect.addEventListener('change', async () => {
            await L.setLanguage(menuLangSelect.value);
            syncAllLangDropdowns(menuLangSelect.value, 'menu-language');
            console.log(`[PnO] Language changed to: ${menuLangSelect.value}`);
        });
    }
}

// Language popup on first visit (matches Unity's startup language selector)
function showLanguagePopupIfNeeded() {
    const hasChosen = localStorage.getItem('PnO_LanguageChosen');
    const hasLang = localStorage.getItem('PnO_Language');
    // Defensive: stale `PnO_LanguageChosen=1` without `PnO_Language` (cache
    // wipe, manual storage edit, version migration where keys diverged)
    // leaves the player silently stuck on EN. Re-show the popup in that case.
    if (hasChosen && !hasLang) {
        try { localStorage.removeItem('PnO_LanguageChosen'); } catch (e) {}
    } else if (hasChosen) {
        return;
    }

    const popup = document.getElementById('language-popup');
    if (!popup) return;

    // Build the flag buttons from the single source before wiring them.
    populateLanguageControls();

    popup.classList.remove('hidden');

    popup.querySelectorAll('.language-popup-btn').forEach(btn => {
        btn.addEventListener('click', async () => {
            const lang = btn.dataset.lang;
            await L.setLanguage(lang);
            localStorage.setItem('PnO_LanguageChosen', '1');
            popup.classList.add('hidden');

            // Update dropdowns to match
            const pauseLang = document.getElementById('pause-language');
            if (pauseLang) pauseLang.value = lang;
            const menuLang = document.getElementById('menu-language');
            if (menuLang) menuLang.value = lang;

            console.log(`[PnO] Language selected: ${lang}`);

            // Chain the combined Welcome modal so it appears in the
            // freshly-selected language rather than racing the picker.
            setTimeout(() => showWelcomeModalIfNeeded(), 250);
        });
    });
}

// ============================================
// Combined Welcome modal — first-launch consolidation.
// One overlay covers: 18+ verification (mandatory), anonymous analytics
// opt-in (optional), and image-vote opt-in (optional). Replaces the
// previous separate age-warning + analytics-consent modals so the user
// answers once instead of clicking through two stacked dialogs.
// Shows when EITHER PnO_SkipAgeWarning is unset OR PnO_AnalyticsConsent
// is unset; on Continue we save all three keys atomically.
// ============================================
export function showWelcomeModalIfNeeded() {
    const ageDone = localStorage.getItem('PnO_SkipAgeWarning') === '1';
    const consentDone = localStorage.getItem('PnO_AnalyticsConsent') !== null;
    // Returning user (already verified + consented): skip the welcome modal but
    // still offer the one-time display-size prompt so upgraders who hit the
    // scaling issue get a chance to set a comfortable size.
    if (ageDone && consentDone) { showScalePromptIfNeeded(); return; }

    const modal = document.getElementById('welcome-modal');
    if (!modal) return;

    // Preload toggles from existing values (returning user with one prior
    // answer, brand-new user defaults to off — privacy-by-default).
    const tAna = document.getElementById('welcome-toggle-analytics');
    const tVote = document.getElementById('welcome-toggle-vote');
    if (tAna) tAna.checked = localStorage.getItem('PnO_AnalyticsConsent') === '1';
    if (tVote) tVote.checked = localStorage.getItem('ImageVoteEnabled') === '1';

    modal.classList.remove('hidden');

    const btnConfirm = document.getElementById('btn-welcome-confirm');
    const btnDeny = document.getElementById('btn-welcome-deny');

    const onConfirm = () => {
        const ana = tAna && tAna.checked ? '1' : '0';
        const vote = tVote && tVote.checked ? '1' : '0';
        try {
            localStorage.setItem('PnO_SkipAgeWarning', '1');
            localStorage.setItem('PnO_AnalyticsConsent', ana);
            localStorage.setItem('ImageVoteEnabled', vote);
            localStorage.removeItem('PnO_PlayerId');
        } catch (e) { console.warn('[Settings] consent persist failed:', e?.message); }

        // Sync any Settings toggles that were already bound.
        const pauseAna = document.getElementById('pause-analytics');
        if (pauseAna) pauseAna.checked = ana === '1';
        const pauseVote = document.getElementById('pause-image-voting');
        if (pauseVote) pauseVote.checked = vote === '1';

        modal.classList.add('hidden');
        console.log(`[PnO] Welcome confirmed: analytics=${ana}, vote=${vote}`);
        // Chain the one-time display-size prompt after consent.
        showScalePromptIfNeeded();
    };
    const onDeny = () => { window.location.href = 'https://google.com'; };

    if (btnConfirm) btnConfirm.addEventListener('click', onConfirm, { once: true });
    if (btnDeny) btnDeny.addEventListener('click', onDeny, { once: true });
}

// ============================================
// First-launch display-size prompt (one-time, gated by PnO_ScalePromptDone).
// Lets every player pick a comfortable Text & UI size up front instead of
// discovering the slider later. The key recovery path now that the desktop
// build renders at native resolution (ignores Windows scaling). The slider
// drives PnO_TextScale live via _applyTextScale; the modal box itself honours
// --overlay-zoom, so dragging gives an instant size preview.
// ============================================
export function showScalePromptIfNeeded() {
    if (localStorage.getItem('PnO_ScalePromptDone') === '1') return;
    const modal = document.getElementById('uiscale-modal');
    if (!modal) { try { localStorage.setItem('PnO_ScalePromptDone', '1'); } catch (e) {} return; }
    if (!modal.classList.contains('hidden')) return; // already open

    const slider = document.getElementById('welcome-textscale');
    const valEl = document.getElementById('welcome-textscale-value');
    const cur = (() => { const v = parseFloat(localStorage.getItem('PnO_TextScale')); return (!Number.isNaN(v) && v > 0) ? v : 1; })();
    if (slider) slider.value = String(Math.round(cur * 100));
    if (valEl) valEl.textContent = Math.round(cur * 100) + '%';

    // Localize the freshly-shown modal (its data-l10n nodes weren't present at
    // the last LOCALE_CHANGED pass if language was just picked).
    try { refreshL10nUI(); } catch (e) {}
    modal.classList.remove('hidden');

    const onInput = () => {
        const pct = parseInt(slider.value, 10);
        const scale = pct / 100;
        if (valEl) valEl.textContent = pct + '%';
        try { localStorage.setItem('PnO_TextScale', String(scale)); } catch (e) {}
        _applyTextScale(scale);
    };
    if (slider) slider.addEventListener('input', onInput);

    const btn = document.getElementById('btn-welcome-scale-confirm');
    const onConfirm = () => {
        try { localStorage.setItem('PnO_ScalePromptDone', '1'); } catch (e) {}
        if (slider) slider.removeEventListener('input', onInput);
        modal.classList.add('hidden');
        // Reflect the chosen value on the Settings sliders.
        try { _applyPersistedTextScale(); } catch (e) {}
    };
    if (btn) btn.addEventListener('click', onConfirm, { once: true });
}

// ============================================
// Cloud Save Sync — settings block (menu + pause copies)
//
// Gated entirely behind cloudSaveManager.isAvailable() (kill-switch stripped 2026-07-07;
// kill-switch). When unavailable the whole block stays hidden. Both the menu
// and pause copies share the same data-cloudsync-* hooks; the two master
// toggles stay mirrored. All listeners go through an events scope per
// web-modules.md; no innerHTML replacement (we toggle attributes / textContent
// only, so no listener orphaning).
//
// The encryption + transport API lives in systems/cloud-save.js. This module
// only drives the DOM and routes user intent into the manager.
// ============================================
let _cloudScope = null;
const _CLOUD_MASKED = '•'.repeat(29); // tiger-velvet-anchor-mango-frost-cabin-ish length

function _cloudBlocks() {
    return Array.from(document.querySelectorAll('.cloudsync-block[data-cloudsync-scope]'));
}

/** Refresh the status line + panel visibility for every cloud block from the
 *  current manager status. Pure read — no user mutation. */
function _cloudRefreshUI() {
    if (!cloudSaveManager.isAvailable()) return;
    let st = {};
    try { st = cloudSaveManager.status() || {}; } catch (e) { st = {}; }
    const enabled = !!st.enabled;
    const hasCode = !!st.hasCode;

    _cloudBlocks().forEach(block => {
        block.classList.remove('hidden');

        const toggle = block.querySelector('[data-cloudsync-toggle]');
        if (toggle) toggle.checked = enabled;

        const panel = block.querySelector('[data-cloudsync-panel]');
        if (panel) panel.hidden = !enabled;

        // Status line
        const statusEl = block.querySelector('[data-cloudsync-status]');
        if (statusEl) {
            let msg = '';
            let cls = 'cloudsync-status';
            if (!enabled) {
                msg = L.get('cloudsync.status_off', 'Off. Your saves stay on this device only.');
                cls += ' cloudsync-status-off';
            } else if (st.lastError) {
                msg = L.get('cloudsync.status_error', 'Last sync failed:') + ' ' + st.lastError;
                cls += ' cloudsync-status-error';
            } else if (st.syncing) {
                msg = L.get('cloudsync.status_syncing', 'Syncing…');
            } else if (st.lastSync) {
                msg = L.get('cloudsync.status_synced', 'Last sync:') + ' ' + _cloudFmtTime(st.lastSync);
                cls += ' cloudsync-status-ok';
            } else {
                msg = L.get('cloudsync.status_ready', 'Sync ready.');
            }
            statusEl.textContent = msg;
            statusEl.className = cls;
        }

        // Masked code display — only show real code after an explicit Reveal.
        const codeEl = block.querySelector('[data-cloudsync-code]');
        if (codeEl && !codeEl.dataset.revealed) {
            codeEl.textContent = hasCode ? _CLOUD_MASKED : '•••';
        }

        // Setup mode (enabled, no code yet): show generate-or-adopt, hide the
        // code display + actions until an identity exists.
        const setupEl = block.querySelector('[data-cloudsync-setup]');
        if (setupEl) setupEl.hidden = !enabled || hasCode;
        const codeRow = block.querySelector('.cloudsync-code-row');
        if (codeRow) codeRow.hidden = !hasCode;
        const codeActions = block.querySelector('.cloudsync-code-actions');
        if (codeActions) codeActions.hidden = !hasCode;
        const codeWarn = block.querySelector('.cloudsync-code-warn');
        if (codeWarn) codeWarn.hidden = !hasCode;
    });
}

function _cloudFmtTime(iso) {
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return String(iso);
        return d.toLocaleString();
    } catch (e) { return String(iso); }
}

/** Mirror a checkbox state across all cloud-sync master toggles. */
function _cloudSyncToggles(checked) {
    _cloudBlocks().forEach(block => {
        const t = block.querySelector('[data-cloudsync-toggle]');
        if (t) t.checked = checked;
    });
}

/** Open the Art. 9 consent modal. Resolves true if the user confirmed
 *  (checkbox ticked + Enable), false on Cancel / X / dismiss. */
function _cloudConsentModal() {
    return new Promise(resolve => {
        const modal = document.getElementById('cloudsync-consent-modal');
        if (!modal) { resolve(false); return; }
        const check = document.getElementById('cloudsync-consent-check');
        const enableBtn = document.getElementById('btn-cloudsync-consent-enable');
        const cancelBtn = document.getElementById('btn-cloudsync-consent-cancel');
        const cancelX = document.getElementById('btn-cloudsync-consent-cancel-x');

        if (check) check.checked = false;
        if (enableBtn) enableBtn.disabled = true;

        const onCheck = () => { if (enableBtn) enableBtn.disabled = !(check && check.checked); };
        const cleanup = (result) => {
            modal.classList.add('hidden');
            if (check) check.removeEventListener('change', onCheck);
            if (enableBtn) enableBtn.removeEventListener('click', onEnable);
            if (cancelBtn) cancelBtn.removeEventListener('click', onCancel);
            if (cancelX) cancelX.removeEventListener('click', onCancel);
            resolve(result);
        };
        const onEnable = () => { if (check && check.checked) cleanup(true); };
        const onCancel = () => cleanup(false);

        if (check) check.addEventListener('change', onCheck);
        if (enableBtn) enableBtn.addEventListener('click', onEnable);
        if (cancelBtn) cancelBtn.addEventListener('click', onCancel);
        if (cancelX) cancelX.addEventListener('click', onCancel);

        modal.classList.remove('hidden');
    });
}

/** Generic confirm dialog (used for delete). Resolves Promise<boolean>. */
function _cloudConfirm(title, body, okLabel) {
    return new Promise(resolve => {
        const overlay = document.createElement('div');
        overlay.className = 'save-confirm-overlay';
        overlay.style.cssText = 'position:fixed;inset:0;background:rgba(8,6,12,0.85);z-index:1100;display:flex;align-items:center;justify-content:center;backdrop-filter:blur(6px);';
        const dialog = document.createElement('div');
        dialog.className = 'save-confirm-dialog';
        dialog.style.cssText = 'max-width:420px;width:90%;background:var(--bg-panel);border:1px solid var(--border-glow);border-radius:12px;padding:24px;color:var(--text);box-shadow:0 20px 60px rgba(0,0,0,0.6);';
        const safe = (s) => String(s).replace(/[<>&"']/g, c => ({ '<':'&lt;','>':'&gt;','&':'&amp;','"':'&quot;',"'":'&#39;' })[c]);
        dialog.innerHTML =
            `<h3 style="margin:0 0 8px 0;color:#ff9aa8;font-size:18px;">${safe(title)}</h3>` +
            `<p style="margin:0 0 16px 0;color:var(--text-body);font-size:14px;line-height:1.5;">${safe(body)}</p>` +
            `<div style="display:flex;gap:12px;justify-content:flex-end;margin-top:20px;">` +
            `<button class="cc-cancel" style="min-height:44px;padding:10px 18px;background:transparent;border:1px solid var(--border);border-radius:8px;color:var(--text-body);font-size:14px;cursor:pointer;">${safe(L.get('ui.cancel', 'Cancel'))}</button>` +
            `<button class="cc-ok" style="min-height:44px;padding:10px 18px;background:rgba(196,80,80,0.25);border:1px solid rgba(220,120,120,0.65);border-radius:8px;color:#ffd0d0;font-size:14px;font-weight:600;cursor:pointer;">${safe(okLabel)}</button>` +
            `</div>`;
        overlay.appendChild(dialog);
        document.body.appendChild(overlay);
        const cleanup = (r) => { overlay.remove(); document.removeEventListener('keydown', onKey); resolve(r); };
        const onKey = (e) => { if (e.key === 'Escape') cleanup(false); };
        dialog.querySelector('.cc-cancel').addEventListener('click', () => cleanup(false));
        dialog.querySelector('.cc-ok').addEventListener('click', () => cleanup(true));
        overlay.addEventListener('click', (e) => { if (e.target === overlay) cleanup(false); });
        document.addEventListener('keydown', onKey);
        setTimeout(() => dialog.querySelector('.cc-cancel')?.focus(), 50);
    });
}

/** One-time "here is your new code" dialog after generation: shows the code
 *  REVEALED with a save-it-now warning + Copy / Save-as-file actions. The
 *  player must explicitly confirm before the dialog closes — the code cannot
 *  be restored by us if lost. Resolves when dismissed. */
function _cloudShowNewCode(code) {
    return new Promise(resolve => {
        const overlay = document.createElement('div');
        overlay.className = 'save-confirm-overlay';
        overlay.style.cssText = 'position:fixed;inset:0;background:rgba(8,6,12,0.85);z-index:1100;display:flex;align-items:center;justify-content:center;backdrop-filter:blur(6px);';
        const dialog = document.createElement('div');
        dialog.className = 'save-confirm-dialog';
        dialog.style.cssText = 'max-width:460px;width:90%;background:var(--bg-panel);border:1px solid var(--border-glow);border-radius:12px;padding:24px;color:var(--text);box-shadow:0 20px 60px rgba(0,0,0,0.6);';
        const safe = (s) => String(s).replace(/[<>&"']/g, c => ({ '<':'&lt;','>':'&gt;','&':'&amp;','"':'&quot;',"'":'&#39;' })[c]);
        dialog.innerHTML =
            `<h3 style="margin:0 0 8px 0;color:#c4b5fd;font-size:18px;">${safe(L.get('cloudsync.newcode_title', 'Your new sync code'))}</h3>` +
            `<code style="display:block;margin:0 0 12px 0;padding:12px;background:rgba(139,92,246,0.08);border:1px solid rgba(139,92,246,0.3);border-radius:8px;font-size:15px;line-height:1.5;word-break:break-word;user-select:all;color:#f0abfc;">${safe(code)}</code>` +
            `<p style="margin:0 0 16px 0;color:#ffd0a0;font-size:13px;line-height:1.5;">${safe(L.get('cloudsync.newcode_body', 'Write it down or save it now. I cannot restore it for you: without this code, your cloud saves cannot be recovered or decrypted.'))}</p>` +
            `<div style="display:flex;gap:10px;justify-content:flex-end;flex-wrap:wrap;">` +
            `<button class="nc-copy" style="min-height:44px;padding:10px 16px;background:transparent;border:1px solid var(--border);border-radius:8px;color:var(--text-body);font-size:14px;cursor:pointer;">${safe(L.get('ui.cloudsync_copy', 'Copy'))}</button>` +
            `<button class="nc-export" style="min-height:44px;padding:10px 16px;background:transparent;border:1px solid var(--border);border-radius:8px;color:var(--text-body);font-size:14px;cursor:pointer;">${safe(L.get('ui.cloudsync_export', 'Save as file'))}</button>` +
            `<button class="nc-done" style="min-height:44px;padding:10px 18px;background:linear-gradient(135deg,#8b5cf6,#a855f7);border:none;border-radius:8px;color:#fff;font-size:14px;font-weight:600;cursor:pointer;">${safe(L.get('cloudsync.newcode_done', 'I saved my code'))}</button>` +
            `</div>`;
        overlay.appendChild(dialog);
        document.body.appendChild(overlay);
        const cleanup = () => { overlay.remove(); resolve(); };
        dialog.querySelector('.nc-copy').addEventListener('click', async () => {
            try {
                if (navigator.clipboard && navigator.clipboard.writeText) await navigator.clipboard.writeText(code);
                else { const ta = document.createElement('textarea'); ta.value = code; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove(); }
                events.emit(Events.TOAST_REQUEST, L.get('cloudsync.code_copied', 'Sync code copied.'), 'toast-success');
            } catch (e) {
                events.emit(Events.TOAST_REQUEST, L.get('cloudsync.code_copy_failed', 'Copy failed. Reveal and copy manually.'), 'toast-error');
            }
        });
        dialog.querySelector('.nc-export').addEventListener('click', () => {
            try { _cloudExportCodeFile(code); } catch (e) {}
        });
        // Deliberately NO backdrop-click / Escape close — the only way out is
        // the explicit "I saved my code" confirmation.
        dialog.querySelector('.nc-done').addEventListener('click', cleanup);
        setTimeout(() => dialog.querySelector('.nc-done')?.focus(), 50);
    });
}

/** Live per-word validation feedback for the adopt input. Marks bad words red
 *  and enables the "Use this code" button only when validatePhrase is ok. */
function _cloudValidateAdopt(block) {
    const input = block.querySelector('[data-cloudsync-adopt-input]');
    const fb = block.querySelector('[data-cloudsync-adopt-feedback]');
    const go = block.querySelector('[data-cloudsync-adopt-go]');
    if (!input) return;
    const raw = input.value || '';
    if (!raw.trim()) {
        if (fb) { fb.textContent = ''; fb.className = 'cloudsync-adopt-feedback'; }
        if (go) go.disabled = true;
        return;
    }
    let res = { ok: false, words: [], badWords: [] };
    try { res = cloudSaveManager.validatePhrase(raw) || res; } catch (e) {}
    const bad = res.badWords || [];
    const words = res.words || raw.trim().toLowerCase().split(/[\s\-\n]+/).filter(Boolean);
    if (fb) {
        // Render each word; bad words get the red class. textContent-based so
        // no untrusted HTML — build spans with createElement.
        fb.textContent = '';
        fb.className = 'cloudsync-adopt-feedback';
        const badSet = new Set(bad.map(w => String(w).toLowerCase()));
        words.forEach((w, i) => {
            const span = document.createElement('span');
            span.className = 'cloudsync-word' + (badSet.has(String(w).toLowerCase()) ? ' cloudsync-word-bad' : ' cloudsync-word-ok');
            span.textContent = w;
            fb.appendChild(span);
            if (i < words.length - 1) fb.appendChild(document.createTextNode(' '));
        });
    }
    if (go) go.disabled = !res.ok;
}

function _cloudExportCodeFile(code) {
    try {
        const blob = new Blob([code + '\n'], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'pinked_sync_code.txt';
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        events.emit(Events.TOAST_REQUEST, L.get('cloudsync.code_exported', 'Sync code saved to file.'), 'toast-success');
    } catch (e) {
        events.emit(Events.TOAST_REQUEST, L.get('cloudsync.code_export_failed', 'Could not save the code file.'), 'toast-error');
    }
}

function initCloudSaveSync() {
    // Kill-switch off → leave the whole block hidden, wire nothing.
    if (!cloudSaveManager.isAvailable()) {
        _cloudBlocks().forEach(b => b.classList.add('hidden'));
        return;
    }

    // Fresh scope each init (idempotent re-init drops the old one).
    if (_cloudScope) { try { _cloudScope.destroy(); } catch (e) {} }
    _cloudScope = events.createScope('cloud-save-settings');

    // Manager-driven refreshes.
    _cloudScope.on(Events.CLOUD_SYNC_STATUS || 'cloud:status', () => _cloudRefreshUI());
    // Re-sync the UI whenever the pause overlay opens (status may have moved on).
    _cloudScope.on(Events.OVERLAY_OPEN, (id) => { if (id === 'overlay-pause') _cloudRefreshUI(); });

    _cloudBlocks().forEach(block => {
        // Master toggle.
        const toggle = block.querySelector('[data-cloudsync-toggle]');
        if (toggle) {
            toggle.addEventListener('change', async () => {
                if (toggle.checked) {
                    // First enable → Art. 9 consent modal.
                    const consented = await _cloudConsentModal();
                    if (!consented) {
                        _cloudSyncToggles(false); // user declined → toggle stays off
                        _cloudRefreshUI();
                        return;
                    }
                    try {
                        await cloudSaveManager.enable();
                        if (cloudSaveManager.hasCode()) {
                            // Re-enable on a device that already has a code →
                            // push the local saves up right away.
                            try { cloudSaveManager.syncAll(); } catch (e) {}
                        }
                        // No code yet → the panel shows setup mode (generate
                        // or adopt). Never auto-generate here: on a second
                        // device the player wants to enter their EXISTING
                        // code, and a throwaway code could push saves under
                        // a wrong identity first.
                    } catch (e) {
                        events.emit(Events.TOAST_REQUEST, L.get('cloudsync.enable_failed', 'Could not enable cloud sync.'), 'toast-error');
                        _cloudSyncToggles(false);
                    }
                    _cloudSyncToggles(cloudSaveManager.isEnabled());
                    _cloudRefreshUI();
                } else {
                    // Disable → ask whether to also delete the server copies
                    // (Art. 7(3) withdrawal as easy as consent).
                    const alsoDelete = await _cloudConfirm(
                        L.get('cloudsync.disable_title', 'Turn off cloud sync?'),
                        L.get('cloudsync.disable_body', 'Your local saves are never affected. Do you also want to delete the copies stored on the server?'),
                        L.get('cloudsync.disable_delete', 'Turn off + delete server copies'),
                    );
                    try { await cloudSaveManager.disable({ deleteRemote: !!alsoDelete }); }
                    catch (e) { events.emit(Events.TOAST_REQUEST, L.get('cloudsync.disable_failed', 'Could not turn off cloud sync.'), 'toast-error'); }
                    _cloudSyncToggles(cloudSaveManager.isEnabled());
                    _cloudRefreshUI();
                }
            });
        }

        // Create-new-code button (setup mode — fresh device, no adopt).
        block.querySelector('[data-cloudsync-generate]')?.addEventListener('click', async () => {
            try {
                const code = await cloudSaveManager.generateCode();
                // One-time reveal + save-it-now warning. The code cannot be
                // restored by us — player must confirm before continuing.
                await _cloudShowNewCode(code);
                // First push so the cloud has this device's saves.
                try { cloudSaveManager.syncAll(); } catch (e) {}
            } catch (e) {
                events.emit(Events.TOAST_REQUEST, L.get('cloudsync.enable_failed', 'Could not enable cloud sync.'), 'toast-error');
            }
            _cloudRefreshUI();
        });

        // Reveal / Copy / Export code buttons.
        const codeEl = block.querySelector('[data-cloudsync-code]');
        block.querySelector('[data-cloudsync-reveal]')?.addEventListener('click', () => {
            if (!codeEl) return;
            let code = '';
            try { code = cloudSaveManager.getCode() || ''; } catch (e) {}
            if (!code) return;
            if (codeEl.dataset.revealed) {
                delete codeEl.dataset.revealed;
                codeEl.textContent = _CLOUD_MASKED;
            } else {
                codeEl.dataset.revealed = '1';
                codeEl.textContent = code;
            }
        });
        block.querySelector('[data-cloudsync-copy]')?.addEventListener('click', async () => {
            let code = '';
            try { code = cloudSaveManager.getCode() || ''; } catch (e) {}
            if (!code) return;
            try {
                if (navigator.clipboard && navigator.clipboard.writeText) await navigator.clipboard.writeText(code);
                else { const ta = document.createElement('textarea'); ta.value = code; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove(); }
                events.emit(Events.TOAST_REQUEST, L.get('cloudsync.code_copied', 'Sync code copied.'), 'toast-success');
            } catch (e) {
                events.emit(Events.TOAST_REQUEST, L.get('cloudsync.code_copy_failed', 'Copy failed. Reveal and copy manually.'), 'toast-error');
            }
        });
        block.querySelector('[data-cloudsync-export]')?.addEventListener('click', () => {
            let code = '';
            try { code = cloudSaveManager.getCode() || ''; } catch (e) {}
            if (code) _cloudExportCodeFile(code);
        });

        // Adopt-code input: live per-word validation.
        const adoptInput = block.querySelector('[data-cloudsync-adopt-input]');
        if (adoptInput) {
            adoptInput.addEventListener('input', () => {
                // Force-lowercase as the player types (mobile autocapitalize guard).
                const pos = adoptInput.selectionStart;
                adoptInput.value = adoptInput.value.toLowerCase();
                try { adoptInput.setSelectionRange(pos, pos); } catch (e) {}
                _cloudValidateAdopt(block);
            });
        }
        block.querySelector('[data-cloudsync-adopt-go]')?.addEventListener('click', async () => {
            const phrase = adoptInput ? adoptInput.value : '';
            if (!phrase.trim()) return;
            // Owner decision 2026-06-05: entering a code = IMPORT. Cloud
            // versions replace the synced slots — warn + confirm first.
            const goAhead = await _cloudConfirm(
                L.get('cloudsync.import_title', 'Import cloud saves?'),
                L.get('cloudsync.import_body', 'This connects the device to the entered code and loads its cloud saves: local saves in the synced slots (Auto-Save and Slot 1; all slots for patrons) are replaced by the cloud versions. Other slots are not affected.'),
                L.get('cloudsync.import_confirm', 'Import'),
            );
            if (!goAhead) return;
            const fb = block.querySelector('[data-cloudsync-adopt-feedback]');
            let res = { ok: false };
            try { res = await cloudSaveManager.adoptCode(phrase); } catch (e) { res = { ok: false, error: e?.message }; }
            if (res && res.ok) {
                const n = (res.foundSaves != null) ? res.foundSaves : 0;
                events.emit(Events.TOAST_REQUEST,
                    L.get('cloudsync.adopt_ok', 'Code accepted. Cloud saves found:') + ' ' + n,
                    'toast-success');
                if (adoptInput) adoptInput.value = '';
                if (fb) { fb.textContent = ''; fb.className = 'cloudsync-adopt-feedback'; }
                // Refresh slot badges + status now that we have a new code's list.
                events.emit(Events.CLOUD_SYNC_LIST_REFRESH || 'cloud:listRefresh');
                _cloudRefreshUI();
            } else {
                if (fb) { fb.textContent = (res && res.error) ? res.error : L.get('cloudsync.adopt_failed', 'That code did not work.'); fb.className = 'cloudsync-adopt-feedback cloudsync-adopt-feedback-error'; }
            }
        });

        // Delete cloud saves.
        block.querySelector('[data-cloudsync-delete]')?.addEventListener('click', async () => {
            const ok = await _cloudConfirm(
                L.get('cloudsync.delete_title', 'Delete cloud saves?'),
                L.get('cloudsync.delete_body', 'This permanently deletes every save stored on the server for your code. Your local saves are not affected. This cannot be undone.'),
                L.get('cloudsync.delete_confirm', 'Delete cloud saves'),
            );
            if (!ok) return;
            try {
                await cloudSaveManager.deleteRemote();
                events.emit(Events.TOAST_REQUEST, L.get('cloudsync.deleted', 'Cloud saves deleted.'), 'toast-success');
                events.emit(Events.CLOUD_SYNC_LIST_REFRESH || 'cloud:listRefresh');
            } catch (e) {
                events.emit(Events.TOAST_REQUEST, L.get('cloudsync.delete_failed', 'Could not delete cloud saves.'), 'toast-error');
            }
            _cloudRefreshUI();
        });
    });

    _cloudRefreshUI();
}

// ============================================
// Settings Toggles
// ============================================
function initStaticImagesToggle() {
    // Image voting opt-in
    const voteToggle = document.getElementById('pause-image-voting');
    if (voteToggle) {
        voteToggle.checked = localStorage.getItem('ImageVoteEnabled') === '1';
        voteToggle.addEventListener('change', () => {
            localStorage.setItem('ImageVoteEnabled', voteToggle.checked ? '1' : '0');
        });
    }
    // Anonymous analytics consent. Toggle is checked = analytics ON.
    // Default is OFF (opt-in required) — the first-run consent modal sets
    // this flag explicitly. Toggling it here counts as an explicit choice.
    // Both the pause (in-game) and menu (main menu) toggles write the same
    // localStorage key and sync each other so the UI stays coherent.
    const analyticsToggles = [
        document.getElementById('pause-analytics'),
        document.getElementById('setting-analytics'),
    ].filter(Boolean);
    const currentConsent = () => localStorage.getItem('PnO_AnalyticsConsent') === '1';
    analyticsToggles.forEach(toggle => {
        toggle.checked = currentConsent();
        toggle.addEventListener('change', () => {
            localStorage.setItem('PnO_AnalyticsConsent', toggle.checked ? '1' : '0');
            // Mirror state on the other toggle so pause + menu stay in sync.
            analyticsToggles.forEach(other => { if (other !== toggle) other.checked = toggle.checked; });
            if (!toggle.checked) {
                // Flip to off: drop any cached rotating-id so nothing carries over.
                for (const key of Object.keys(localStorage)) {
                    if (key.startsWith('PnO_AnalyticsSid_') || key === 'PnO_PlayerId') {
                        try { localStorage.removeItem(key); } catch(e) {}
                    }
                }
            }
        });
    });
    // Reduce Motion toggle (both menu + pause)
    const reduceMotionDefault = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? '1' : '0';
    for (const id of ['setting-reduce-motion', 'pause-reduce-motion']) {
        const el = document.getElementById(id);
        if (el) {
            el.checked = (localStorage.getItem('PnO_ReduceMotion') || reduceMotionDefault) === '1';
            el.addEventListener('change', () => {
                localStorage.setItem('PnO_ReduceMotion', el.checked ? '1' : '0');
                // Sync both toggles
                for (const otherId of ['setting-reduce-motion', 'pause-reduce-motion']) {
                    const other = document.getElementById(otherId);
                    if (other && other !== el) other.checked = el.checked;
                }
            });
        }
    }
    // Auto Scroll toggle (both menu + pause). Default OFF — reader-paced
    // players who scroll top-down through new content lose context when the
    // panel jumps. Opt-in for players who prefer the auto-jump.
    // Foundby:Kiran v0.2.5.8.1.
    const autoScrollEnabled = localStorage.getItem('PnO_AutoScroll') === '1';
    for (const id of ['setting-auto-scroll', 'pause-auto-scroll']) {
        const el = document.getElementById(id);
        if (el) {
            el.checked = autoScrollEnabled;
            el.addEventListener('change', () => {
                localStorage.setItem('PnO_AutoScroll', el.checked ? '1' : '0');
                for (const otherId of ['setting-auto-scroll', 'pause-auto-scroll']) {
                    const other = document.getElementById(otherId);
                    if (other && other !== el) other.checked = el.checked;
                }
            });
        }
    }

    // Stat Preview toggle (both menu + pause). Default OFF for NEW players,
    // ON for existing players (detected by presence of any PnO_* save-related key).
    // Rationale: new players get uncluttered roleplay, existing optimizers keep their previews.
    if (localStorage.getItem('PnO_StatPreview') === null) {
        const hasExistingSave = Object.keys(localStorage).some(k =>
            k === 'PnO_SaveSlot1' || k === 'PnO_SaveSlot2' || k === 'PnO_SaveSlot3' || k === 'PnO_AutoSave'
        );
        localStorage.setItem('PnO_StatPreview', hasExistingSave ? '1' : '0');
    }
    const statPreviewEnabled = localStorage.getItem('PnO_StatPreview') === '1';
    document.body.classList.toggle('hide-stat-previews', !statPreviewEnabled);
    for (const id of ['setting-stat-preview', 'pause-stat-preview']) {
        const el = document.getElementById(id);
        if (el) {
            el.checked = statPreviewEnabled;
            el.addEventListener('change', () => {
                localStorage.setItem('PnO_StatPreview', el.checked ? '1' : '0');
                document.body.classList.toggle('hide-stat-previews', !el.checked);
                for (const otherId of ['setting-stat-preview', 'pause-stat-preview']) {
                    const other = document.getElementById(otherId);
                    if (other && other !== el) other.checked = el.checked;
                }
            });
        }
    }

    // Scene Mode toggle (both menu + pause). Default ON.
    // When OFF, narrative scenes keep sidebars expanded (no auto-collapse).
    const sceneModeEnabled = (localStorage.getItem('PnO_SceneMode') ?? '1') === '1';
    document.body.classList.toggle('scene-mode-disabled', !sceneModeEnabled);
    for (const id of ['setting-scene-mode', 'pause-scene-mode']) {
        const el = document.getElementById(id);
        if (el) {
            el.checked = sceneModeEnabled;
            el.addEventListener('change', () => {
                localStorage.setItem('PnO_SceneMode', el.checked ? '1' : '0');
                document.body.classList.toggle('scene-mode-disabled', !el.checked);
                // If user just disabled it, drop scene-mode from body and clear collapsed state
                if (!el.checked) {
                    document.body.classList.remove('scene-mode');
                    document.getElementById('left-column')?.classList.remove('user-expanded');
                    document.getElementById('right-column')?.classList.remove('user-expanded');
                }
                // Sync both toggles
                for (const otherId of ['setting-scene-mode', 'pause-scene-mode']) {
                    const other = document.getElementById(otherId);
                    if (other && other !== el) other.checked = el.checked;
                }
            });
        }
    }

    // Cloud Save Sync block — part of the consent cluster. Self-gates on the
    // kill-switch / availability; hides itself when unavailable.
    try { initCloudSaveSync(); } catch (e) { console.warn('[Settings] cloud-save init failed:', e?.message); }
}

// ============================================
// SMS / chat mute toggles (Notifications section)
// Bound directly to Ink VARs (mute_master_sms / mute_dating_sms /
// mute_cuck_sms / mute_dareme). Default false; legacy saves auto-default
// because new VARs read as false on missing-key access.
// ============================================
function _muteStory() {
    return variableManager && variableManager._story ? variableManager._story : null;
}

function _readMute(varName) {
    const story = _muteStory();
    if (!story) return false;
    try {
        const v = story.variablesState[varName];
        return v === true || v === 1;
    } catch (e) { return false; }
}

function _writeMute(varName, value) {
    const story = _muteStory();
    if (!story) return;
    try { story.variablesState[varName] = !!value; }
    catch (e) { console.warn(`[Settings] mute write '${varName}' failed:`, e?.message); }
}

function _syncMuteToggles() {
    document.querySelectorAll('input[type="checkbox"][data-mute-var]').forEach(el => {
        const varName = el.getAttribute('data-mute-var');
        if (!varName) return;
        el.checked = _readMute(varName);
    });
}

function initMuteToggles() {
    document.querySelectorAll('input[type="checkbox"][data-mute-var]').forEach(el => {
        const varName = el.getAttribute('data-mute-var');
        if (!varName) return;
        // Initial sync (story may not exist yet — _readMute returns false safely)
        el.checked = _readMute(varName);
        el.addEventListener('change', () => {
            _writeMute(varName, el.checked);
        });
    });
}

// ============================================
// Content Preferences (0.3, CONTENT_TOGGLES) — mid-run pause surface
// ============================================
// Reveal + (re)build the pause content-prefs section. Gated on the LIVE Ink
// v030_content_enabled (story exists mid-run): hidden + inert while dormant,
// revealed + rebuilt when armed. Called DIRECTLY from the pause-open handler
// (main.js) because openOverlay('overlay-pause') does NOT emit OVERLAY_OPEN —
// same reason the cloud/lovense/mute panels are refreshed manually there. The
// OVERLAY_OPEN listener below is kept for the emit-based open paths.
function refreshContentPrefsPause() {
    const section = document.getElementById('content-prefs-section-pause');
    if (!section) return;
    const story = variableManager && variableManager._story ? variableManager._story : null;
    // Hide entirely for free players when every track is Patreon-only (nothing to
    // configure) — same rule as the editor surface.
    if (story && contentMenuEnabled(story) && hasVisibleContentTracks(!isPat())) {
        section.style.display = '';
        try { initContentPrefToggles(story); }
        catch (e) { console.warn('[Settings] content-prefs init failed:', e?.message); }
    } else {
        section.style.display = 'none';
    }
}

function initContentPrefsPause() {
    events.on(Events.OVERLAY_OPEN, (id) => {
        if (id !== 'overlay-pause') return;
        refreshContentPrefsPause();
    });
}

// ============================================
// Arousal Breathing Animation
// ============================================
function initArousalBreathing() {
    events.on(Events.STAT_CHANGED, (stat, value) => {
        if (stat !== 'Arousal') return;
        const charPanel = document.querySelector('.char-panel');
        const arousalBar = document.getElementById('bar-arousal-fill');
        if (value >= 80) {
            charPanel?.classList.add('arousal-breathing');
            arousalBar?.classList.add('arousal-glow');
        } else {
            charPanel?.classList.remove('arousal-breathing');
            arousalBar?.classList.remove('arousal-glow');
        }
    });
}

export const settingsUI = {
    refreshL10n: refreshL10nUI,
    bindSliders: bindSettingsSliders,
    showLanguagePopup: showLanguagePopupIfNeeded,
    showWelcomeModal: showWelcomeModalIfNeeded,
    showScalePrompt: showScalePromptIfNeeded,
    initToggles: initStaticImagesToggle,
    initMuteToggles,
    initContentPrefsPause,
    refreshContentPrefsPause,
    syncMuteToggles: _syncMuteToggles,
    initArousalBreathing,
    initCloudSaveSync,
    refreshCloudSaveUI: _cloudRefreshUI,
};
